import React from 'react';
import { useTranslation } from 'react-i18next';
import type { JournalEntry } from '@/api/models';
import {
    useChartOfAccounts,
    useJournalEntriesQuery,
    usePrepareVatSettlementMutation,
} from '@/api/queries';
import { resolveLocale } from '@/pages/accounting/statements/statementMeta';
import {
    settlementBlock,
    settlementDueDate,
    settlementEntryDate,
    type SettlementBlock,
} from '../../vatMeta';

type UseVatSettlementParams = {
    fromDate: string;
    toDate: string;
    netTetri: number;
    /** From the report. `null` = the ledger has no settlement account. */
    settlementAccountCode: string | null;
    /** Refetch the report after a settlement posts. */
    onSettled: () => void;
};

export type UseVatSettlementResult = {
    /** Why the action is unavailable, or `null` when it can be pressed. */
    block: SettlementBlock;
    isPreparing: boolean;
    /** The draft currently open in the Journal Entry form (null = closed). */
    draft: JournalEntry | null;
    /**
     * How many entries are already posted in this period against the settlement
     * account.
     *
     * This is a **look-alike check, not a guarantee**. The server enforces no
     * duplicate-settlement rule — two builds for the same period return two
     * different entries, and nothing is recorded in between — and a posted
     * settlement is not even marked as one, since `source` comes back `MANUAL`.
     * So all this can honestly say is that something has already moved the
     * settlement account in this period, and the copy says exactly that rather
     * than implying a second settlement would be refused.
     */
    existingCount: number;
    /** The settlement account's id, for the "review it" drill. `null` if unresolved. */
    existingAccountId: string | null;
    prepareError: string | null;
    /** The 15th of the month after the period ends. Informational only. */
    dueDate: string;
    onPrepare: () => Promise<void>;
    onClose: () => void;
    onSaved: (entry: JournalEntry) => void;
};

/**
 * State machine behind the VAT settlement action. Owns the build mutation and
 * the draft lifecycle so the component stays render-only.
 *
 * Three properties are load-bearing, and none are cosmetic:
 *
 * 1. **It builds a DRAFT and never posts.** The server's endpoint is a builder
 *    that persists nothing; the draft goes to the shared entry form, which saves
 *    and posts it through the same validated path as any manual entry. A closed
 *    period is refused *there*, by the form's own period gate — not
 *    special-cased here, and deliberately not trusted from the draft's own
 *    `validationState`, which used to report a closed-period draft as postable.
 * 2. **`onSaved` settles only on a POST.** A save-draft leaves the form open and
 *    the report untouched, because nothing has moved yet.
 * 3. **The duplicate check is a detection, not a promise.** See `existingCount`.
 */
export const useVatSettlement = ({
    fromDate,
    toDate,
    netTetri,
    settlementAccountCode,
    onSettled,
}: UseVatSettlementParams): UseVatSettlementResult => {
    const { t, i18n } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);
    const prepare = usePrepareVatSettlementMutation();

    const [draft, setDraft] = React.useState<JournalEntry | null>(null);
    const [prepareError, setPrepareError] = React.useState<string | null>(null);

    // The report names the settlement account by **code**; the journal filters
    // by id. The chart is the only thing that maps one to the other, and it is
    // already cached — the entry form reads it too.
    const { data: accounts } = useChartOfAccounts({ locale });
    const settlementAccountId = React.useMemo(() => {
        if (!settlementAccountCode || !accounts) {
            return null;
        }
        return (
            accounts.find((account) => account.code === settlementAccountCode)
                ?.id ?? null
        );
    }, [accounts, settlementAccountCode]);

    // One row is enough — only the count is read.
    const existingQuery = useJournalEntriesQuery(
        {
            page: 1,
            pageSize: 1,
            status: 'POSTED',
            fromDate,
            toDate,
            accountId: settlementAccountId ?? undefined,
        },
        settlementAccountId !== null,
    );
    const existingCount =
        existingQuery.data?.success === true
            ? existingQuery.data.response.total
            : 0;

    React.useEffect(() => {
        setPrepareError(null);
    }, [fromDate, toDate]);

    const onPrepare = React.useCallback(async () => {
        setPrepareError(null);
        const result = await prepare.mutateAsync({
            dateFrom: fromDate,
            dateTo: toDate,
            // Always explicit: an omitted date comes back as `0001-01-01`.
            date: settlementEntryDate(toDate),
            locale,
            // No `source` — the server assigns it. See BuildVatSettlementRequest.
        });
        if (!result.success) {
            setPrepareError(
                result.error.message || t('vat.settlement.prepareFailed'),
            );
            return;
        }
        setDraft(result.response);
    }, [prepare, fromDate, toDate, locale, t]);

    const onClose = React.useCallback(() => setDraft(null), []);

    const onSaved = React.useCallback(
        (entry: JournalEntry) => {
            // Only a POST settles the period; a save-draft leaves the form open.
            if (entry.status === 'POSTED') {
                setDraft(null);
                onSettled();
            }
        },
        [onSettled],
    );

    return {
        block: settlementBlock(netTetri, settlementAccountCode),
        isPreparing: prepare.isPending,
        draft,
        existingCount,
        existingAccountId: settlementAccountId,
        prepareError,
        dueDate: settlementDueDate(toDate),
        onPrepare,
        onClose,
        onSaved,
    };
};
